import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { retry, Subscription, throwError, timer } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  ISimConEstado,
  SimsService,
} from '../../../../../auxiliares/servicios/http/sims.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import {
  PIEZAS_DETALLE,
  TonoEstado,
} from '../../../../../componentes/detalle/detalle.components';
import { ListadoAsignacionsComponent } from '../../../../standalone/asignacions/listado-asignacions/listado-asignacions.component';
import { haceCuanto } from '../../../../../auxiliares/estado-conexion.util';
import { CrearEditarSimComponent } from '../crear-editar-sim/crear-editar-sim.component';
import {
  chipEstadoSim,
  linksEquiposSim,
} from '../listado-sims/listado-sims.component';

/** Un equipo donde está puesta la SIM, con lo que hace falta para el link. */
interface EquipoSim {
  nombre: string;
  ruta?: string[];
  abrir?: () => void;
  desde?: string;
}

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaSim {
  estado: { tono: TonoEstado; etiqueta: string; detalle?: string };
  equipos: EquipoSim[];
  /** Fecha en que el equipo informó el ICCID (solo GT06 / TopFlyTech). */
  iccidInformado?: { fecha: string; hace: string };
}

@Component({
  selector: 'app-detalles-sim',
  standalone: true,
  imports: [AuxiliaresModule, ListadoAsignacionsComponent, ...PIEZAS_DETALLE],
  templateUrl: './detalles-sim.component.html',
  styleUrls: [
    '../../../../../componentes/detalle/detalle-pagina.scss',
    './detalles-sim.component.scss',
  ],
})
export class DetallesSimComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  service = inject(SimsService);
  private lista2 = inject(Lista2Service);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);

  public idSim = '';
  public sim?: ISimConEstado;
  public vista?: VistaSim;
  private sim$?: Subscription;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  public loading = true;
  public selectedTab = 0;
  /** La contraseña del APN se muestra recién al pedirla. */
  public verPassword = false;

  public get titulo(): string {
    return SimsService.nombre(this.sim);
  }

  public get subtitulo(): string {
    // Si el título ya es el número (SIM sin ICCID), no repetirlo
    const numero = this.sim?.iccid ? this.sim?.numero : undefined;
    return [numero, this.sim?.operador].filter(Boolean).join(' · ');
  }

  /** El cliente, sin HTML (el listado lo arma con la jerarquía). */
  public get cliente(): string | undefined {
    if (HelperService.getTipoCliente() === 'Final') return undefined;
    const cadena = this.clienteJerarquia.cadena(this.sim?.idCliente);
    return cadena[cadena.length - 1]?.nombre || undefined;
  }

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return ['info', 'asignaciones'];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  /**
   * Estado con el criterio del listado (chipEstadoSim): disponible, asignada
   * o en conflicto (puesta en más de un equipo).
   */
  private armarVista(): void {
    const sim = this.sim;
    if (!sim) return;
    const chip = chipEstadoSim(sim);
    const tonos: Record<string, TonoEstado> = {
      Disponible: 'ok',
      Asignada: 'neutro',
      'En conflicto': 'alerta',
    };
    const equipos: EquipoSim[] = linksEquiposSim(sim).map((link, i) => {
      const desde = sim.asignacionesVigentes?.[i]?.fechaAsignacion;
      return {
        nombre: link.link,
        ruta: Array.isArray(link.route) ? link.route : undefined,
        abrir: link.click,
        desde: desde ? HelperService.getFechaYHora(desde) : undefined,
      };
    });
    this.vista = {
      estado: {
        tono: tonos[chip.link] ?? 'neutro',
        etiqueta: chip.link,
        detalle: chip.tooltip,
      },
      equipos,
      // Sólo lo completa el tracker al informar su ICCID (GT06 / TopFlyTech):
      // no es la última comunicación del equipo. Sin dato no se muestra.
      iccidInformado: sim.fechaUltimoReporte
        ? {
            fecha: HelperService.getFechaYHora(sim.fechaUltimoReporte),
            hace: haceCuanto(sim.fechaUltimoReporte),
          }
        : undefined,
    };
  }

  public irA(equipo: EquipoSim): void {
    if (equipo.ruta) this.router.navigate(equipo.ruta);
    else equipo.abrir?.();
  }

  public listarSim(): void {
    this.sim$?.unsubscribe();
    this.sim$ = this.lista2
      .sim(this.idSim, 30)
      // Un request que falla terminaba el refresco de cada 30 s para siempre.
      // Si todavía no se cargó nunca, el error sigue de largo.
      .pipe(
        retry({
          delay: (error) => (this.sim ? timer(30000) : throwError(() => error)),
        }),
      )
      .subscribe({
        next: (data) => {
          this.sim = data;
          this.armarVista();
          this.loading = false;
        },
        error: (error) => {
          this.helper.notifError(error);
          this.loading = false;
        },
      });
  }

  public editar(): void {
    if (!this.sim) return;
    this.formularios
      .abrir(CrearEditarSimComponent, {
        tamano: 'mediano',
        data: { edit: this.sim },
      })
      .afterClosed()
      .subscribe((res) => {
        // Refrescar ya: el polling es cada 30 s
        if (res) this.listarSim();
      });
  }

  /**
   * Carga la SIM del id de la ruta. Se llama cada vez que cambia el id:
   * Angular reutiliza el componente al ir de una SIM a otra (desde el detalle
   * de un equipo, por ejemplo).
   */
  private cargar(id: string | null): void {
    this.loading = true;
    this.sim = undefined;
    this.vista = undefined;
    this.verPassword = false;
    this.idSim = id ?? '';
    this.listarSim();
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((qp) => {
      const indice = qp['tab'] ? this.indicePestana(qp['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.sim$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
